import { ChangeDetectionStrategy, Component, ViewEncapsulation } from '@angular/core';
import { FinancialAccount, Loan, OriginationBorrower, RequestStatus } from '@ld.consumer-portal/models';
import { AssetSummaryModalComponent } from '../asset-summary-modal/asset-summary-modal.component';
import { BankSelectionModalComponent } from '../bank-selection-modal/bank-selection-modal.component';
import {
  BorrowerIdentityData,
  BorrowerIdentityModalComponent,
} from '../borrower-identity-modal/borrower-identity-modal.component';
import { ConfirmAddressModalComponent } from '../confirm-address-modal/confirm-address-modal.component';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { CongratulationsModalComponent } from '../congratulations-modal/congratulations-modal.component';
import { CreditCardModalComponent } from '../credit-card-modal/credit-card-modal.component';
import { DeleteAssetsModalComponent } from '../delete-assets-modal/delete-assets-modal.component';
import { DeletePaymentModalComponent } from '../delete-payment-modal/delete-payment-modal.component';
import { DeleteUserProfileModalComponent } from '../delete-user-profile-modal/delete-user-profile-modal.component';
import { EconsentDisclosureModalComponent } from '../econsent-disclosure-modal/econsent-disclosure-modal.component';
import { ErrorModalComponent } from '../error-modal/error-modal.component';
import { ForceUpdateModalComponent } from '../force-update-modal/force-update-modal.component';
import { GoogleMapsModalComponent } from '../google-maps-modal/google-maps-modal.component';
import { HelocEligibilityPopupComponent } from '../heloc-eligibility-popup/heloc-eligibility-popup.component';
import { InsuranceProviderSelectionModalComponent } from '../insurance-provider-selection-modal/insurance-provider-selection-modal.component';
import { InsuranceTransitionModalComponent } from '../insurance-transition-modal/insurance-transition-modal.component';
import { LinkLoanMfaModalComponent } from '../link-loan-mfa-modal/link-loan-mfa-modal.component';
import { LinkLoanModalComponent } from '../link-loan-modal/link-loan-modal.component';
import { LoanOfficerSearchModalComponent } from '../loan-officer-search-modal/loan-officer-search-modal.component';
import { LoanSelectionModalComponent } from '../loan-selection-modal/loan-selection-modal.component';
import { MaintenanceModalComponent } from '../maintenance-modal/maintenance-modal.component';
import { MessageSuccessModalComponent } from '../message-success-modal/message-success-modal.component';
import { NewServicingLoanModalComponent } from '../new-servicing-loan-modal/new-servicing-loan-modal.component';
import { MissingInformationModalComponent } from '../missing-information-modal/missing-information-modal.component';
import { PaperlessElectronicCommunicationsDisclosureComponent } from '../paperless-electronic-communications-disclosure/paperless-electronic-communications-disclosure.component';
import { PaperlessPromoModalComponent } from '../paperless-promo-modal/paperless-promo-modal.component';
import { PaperlessPromptModalComponent } from '../paperless-prompt-modal/paperless-prompt-modal.component';
import { PartnerDisclosureModalComponent } from '../partner-disclosure-modal/partner-disclosure-modal.component';
import { PaymentElectronicCommunicationsDisclosureComponent } from '../payment-electronic-communications-disclosure/payment-electronic-communications-disclosure.component';
import { PaymentReviewData, PaymentReviewModalComponent } from '../payment-review-modal/payment-review-modal.component';
import { PhoneAuthorizationModalComponent } from '../phone-authorization-modal/phone-authorization-modal.component';
import { PiitTransferModalComponent } from '../piit-transfer-modal/piit-transfer-modal.component';
import { RequestCallbackModalComponent } from '../request-callback-modal/request-callback-modal.component';
import { ReviewContactInfoModalComponent } from '../review-contact-info-modal/review-contact-info-modal.component';
import { SelectAccountsModalComponent } from '../select-accounts-modal/select-accounts-modal.component';
import { ServisBotPromptModalComponent } from '../servisbot-prompt-modal/servisbot-prompt-modal.component';
import { SessionTimeoutModalComponent } from '../session-timeout-modal/session-timeout-modal.component';
import { SwitchBorrowerModalComponent } from '../switch-borrower-modal/switch-borrower-modal.component';
import { TcpaAdditionalLoanModalComponent } from '../tcpa-additional-loan-modal/tcpa-additional-loan-modal.component';
import { TermsAndConditionsModalComponent } from '../terms-and-conditions-modal/terms-and-conditions-modal.component';
import { TermsOfUseModalComponent } from '../terms-of-use-modal/terms-of-use-modal.component';
import { UnconsentModalComponent } from '../unconsent-modal/unconsent-modal.component';
import { UnsavedChangesModalComponent } from '../unsaved-changes-modal/unsaved-changes-modal.component';
import { UploadDocumentModalComponent } from '../upload-document-modal/upload-document-modal.component';
import { VerifyBorrowerModalComponent } from '../verify-borrower-modal/verify-borrower-modal.component';

@Component({
  selector: 'ld-modal-gallery',
  templateUrl: './modal-gallery.component.html',
  styleUrl: './modal-gallery.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    AssetSummaryModalComponent,
    BankSelectionModalComponent,
    BorrowerIdentityModalComponent,
    ConfirmAddressModalComponent,
    ConfirmationModalComponent,
    CongratulationsModalComponent,
    CreditCardModalComponent,
    DeleteAssetsModalComponent,
    DeletePaymentModalComponent,
    DeleteUserProfileModalComponent,
    EconsentDisclosureModalComponent,
    PaperlessElectronicCommunicationsDisclosureComponent,
    PaymentElectronicCommunicationsDisclosureComponent,
    ErrorModalComponent,
    ForceUpdateModalComponent,
    GoogleMapsModalComponent,
    HelocEligibilityPopupComponent,
    InsuranceProviderSelectionModalComponent,
    InsuranceTransitionModalComponent,
    LinkLoanMfaModalComponent,
    LinkLoanModalComponent,
    LoanOfficerSearchModalComponent,
    LoanSelectionModalComponent,
    MaintenanceModalComponent,
    MessageSuccessModalComponent,
    NewServicingLoanModalComponent,
    MissingInformationModalComponent,
    PaperlessPromoModalComponent,
    PaperlessPromptModalComponent,
    PartnerDisclosureModalComponent,
    PaymentReviewModalComponent,
    PhoneAuthorizationModalComponent,
    PiitTransferModalComponent,
    RequestCallbackModalComponent,
    ReviewContactInfoModalComponent,
    SelectAccountsModalComponent,
    ServisBotPromptModalComponent,
    SessionTimeoutModalComponent,
    SwitchBorrowerModalComponent,
    TcpaAdditionalLoanModalComponent,
    TermsAndConditionsModalComponent,
    TermsOfUseModalComponent,
    UnconsentModalComponent,
    UnsavedChangesModalComponent,
    UploadDocumentModalComponent,
    VerifyBorrowerModalComponent,
  ],
})
export class ModalGalleryComponent {
  readonly mockPaymentReviewData: PaymentReviewData = {
    totalAmount: 1525.43,
    paymentDate: new Date('2026-03-01'),
    dueDate: new Date('2026-03-15'),
    bankAccountDisplay: 'Checking - ****1234',
    accountHolderName: 'John Doe',
    lastFourDigits: '1234',
    loanNumber: '1234567890',
  };

  readonly mockAgents = [
    {
      id: '1',
      name: 'Jane Smith',
      role: 'loan-officer' as const,
      roleDisplayName: 'Loan Officer',
      phone: '(800) 555-1234',
      email: 'jane.smith@loandepot.com',
    },
    {
      id: '2',
      name: 'Bob Johnson',
      role: 'processor' as const,
      roleDisplayName: 'Loan Processor',
      phone: '(800) 555-5678',
      email: 'bob.johnson@loandepot.com',
    },
  ];

  readonly mockAccounts: FinancialAccount[] = [
    {
      accountId: 'acc-1',
      accountNumber: '123456789',
      nickName: 'Primary Checking',
      institutionName: 'Chase Bank',
      balance: 15000,
      providerInstitutionId: 'chase',
      shared: true,
    },
    {
      accountId: 'acc-2',
      accountNumber: '987654321',
      nickName: 'Savings Account',
      institutionName: 'Chase Bank',
      balance: 50000,
      providerInstitutionId: 'chase',
      shared: true,
    },
  ];

  readonly mockBorrowers: OriginationBorrower[] = [
    { id: 'b-1', firstName: 'John', lastName: 'Doe', email: 'john.doe@example.com' },
    { id: 'b-2', firstName: 'Jane', lastName: 'Doe', email: 'jane.doe@example.com' },
  ];

  readonly mockBorrowerIdentity: BorrowerIdentityData[] = [
    { id: 'b-1', firstName: 'John', lastName: 'Doe', isPrimary: true, verifyRequired: true },
    { id: 'b-2', firstName: 'Jane', lastName: 'Doe', isPrimary: false, verifyRequired: false },
  ];

  readonly mockPopularInstitutions = [
    { providerInstitutionId: 'chase', name: 'Chase' },
    { providerInstitutionId: 'bofa', name: 'Bank of America' },
    { providerInstitutionId: 'wells', name: 'Wells Fargo' },
  ];

  readonly mockLoans = [
    {
      loanNumber: '1234567890',
      property: { streetAddress: '123 Main St', city: 'Foothill Ranch', state: 'CA', zipCode: '92610' },
      loanBalance: { currentBalance: 350000 },
      paymentDue: 1525.43,
      isActive: true,
    },
  ] as unknown as Loan[];

  readonly mockTransferDate = new Date('2026-04-01');

  readonly mockTodoItem = {
    uniqId: 'todo-1',
    requestOrder: 1,
    requestId: 'req-1',
    requestStatus: RequestStatus.REQUESTED,
    requestedOn: '2026-01-15T00:00:00Z',
    borrowerId: 'b-1',
    documentName: 'W-2 Tax Document',
    documentNotes: 'Please upload your most recent W-2 form.',
  };


  readonly mockSearchByNameFn = () => Promise.resolve([]);
  readonly mockSearchByZipCodeFn = () => Promise.resolve([]);
  readonly mockGetLoanOfficersByBranchFn = () => Promise.resolve([]);

  readonly mockLookupZipFn = () => Promise.resolve({ city: 'Irvine', state: 'CA' });
  readonly mockSubmitPaymentFn = () => Promise.resolve({ isSuccess: true, transactionId: 'mock-tx' });

  readonly mockGetStateLicenseVerbiageFn = () => Promise.resolve({ stateLicenseVerbiage: 'Mock state license verbiage text.' });
  readonly mockGetInsuranceRedirectUrlFn = () => Promise.resolve({ redirectUrl: 'https://example.com/insurance' });
  readonly mockGetMelloInsurancePortalUrlFn = () => Promise.resolve({ redirectUrl: 'https://example.com/mello' });

  noop(): void {
    // no-op handler for gallery event bindings
  }
}
